if (!customElements.get('quick-search-by-metafields')) {
  const ICON_CARET =
    '<svg width="8" height="5" viewBox="0 0 8 5" fill="none"><path d="M8 0.698572L4 5L0 0.698572L0.649619 0L4 3.60286L7.35038 0L8 0.698572Z" fill="currentColor"/></svg>';

  class QuickSearchByMetafields extends HTMLElement {
    connectedCallback() {
      if (this.dataset.quickSearchReady === 'true') return;
      this.dataset.quickSearchReady = 'true';

      this.catalog = this.querySelector('catalog-manager-fitment');
      this.groups = [...this.querySelectorAll('[data-catalog-manager-group]')];
      this.tabsList = this.closest('.tabs')?.querySelector('.tabs__list');
      this.headingSize = this.dataset.headingSize;
      this.headingUnderlined = this.dataset.headingUnderlined === 'true';
      this.tabsAlignment = this.dataset.tabsAlignment;
      this.layout = this.dataset.layout;
      this.buttonStyle = this.dataset.buttonStyle;
      this.showButtonIcon = this.dataset.buttonIcon === 'true';
      this.searchPlaceholder = this.dataset.searchPlaceholder;
      this.noResultsText = this.dataset.noResults;
      this.closeLabel = this.dataset.closeLabel;
      this.resetLabel = this.dataset.resetLabel;
      this.formWidth = this.dataset.formWidth;
      this.formWidthVertical = this.dataset.formWidthVertical;
      this.iconCheck = this.dataset.iconCheck;
      this.iconClose = this.dataset.iconClose;
      this.iconSearch = this.dataset.iconSearch;
      this.iconReset = this.dataset.iconReset;
      if (!this.catalog || !this.tabsList || !this.groups.length) {
        this.classList.add('is-ready');
        return;
      }

      this.buildOriginalStructure();
      this.handleChange = this.handleChange.bind(this);
      this.handleSelect = this.handleSelect.bind(this);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('name-option:select', this.handleSelect);

      this.observer = new MutationObserver((mutations) => {
        const hasFilterMutation = mutations.some(({ target }) =>
          target.matches?.('select[data-catalog-manager-filter-field]') ||
          target.matches?.('option')
        );

        if (hasFilterMutation) this.refreshControls();
      });
      this.observer.observe(this.catalog, {
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'selected']
      });
      this.refreshControls();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('name-option:select', this.handleSelect);
      this.observer?.disconnect();
    }

    switchToBlockTab(blockId) {
      if (!blockId || !this.panels) return;
      const groups = [...this.querySelectorAll('[data-catalog-manager-group]')];
      let target = groups.find((group) => group.dataset.catalogManagerGroup === blockId);
      if (!target) {
        target = [...this.querySelectorAll('select[data-field-key]')].find((select) => select.dataset.fieldKey === blockId);
      }
      const content = target?.closest('.tabs__tab-content');
      const index = content ? this.panels.indexOf(content) : -1;
      if (index < 0) return;
      const activeIndex = this.tabs.findIndex((tab) => tab.classList.contains('tabs__tab--active'));
      if (index !== activeIndex) this.activateTab(index);
    }

    buildOriginalStructure() {
      this.tabs = [];
      this.panels = [];
      this.descriptions = [];

      const descriptionWrapper = document.createElement('div');
      descriptionWrapper.className = 'heading-wrapper__description tabs__description text-size-1';
      let hasSubheading = false;

      this.groups.forEach((group, index) => {
        const title = group.querySelector('h3')?.textContent.trim() || 'Filter group';
        const tab = document.createElement('h2');
        tab.className = `tabs__tab mb-0 ${this.headingSize}${this.headingUnderlined ? ' underline-animated' : ''}${index === 0 ? ' tabs__tab--active' : ''}`;
        tab.tabIndex = index === 0 ? 0 : -1;
        tab.setAttribute('role', 'tab');
        tab.dataset.tabIndex = String(index + 1);
        tab.textContent = title;
        this.tabsList.appendChild(tab);

        const subheading = group.querySelector('.catalog-manager-group__subheading')?.innerHTML.trim() || '';
        if (subheading) {
          const description = document.createElement('span');
          description.dataset.tabIndex = String(index + 1);
          description.className = index === 0 ? '' : 'hidden';
          description.innerHTML = subheading;
          descriptionWrapper.appendChild(description);
          this.descriptions.push(description);
          hasSubheading = true;
        } else {
          this.descriptions.push(null);
        }

        const content = document.createElement('div');
        content.className = `tabs__tab-content${index === 0 ? ' tabs__tab-content--active' : ' hidden'}`;
        content.setAttribute('role', 'tabpanel');

        const appear = document.createElement('div');
        appear.className = 'option-select-appear';
        appear.dataset.animate = 'stages';
        appear.dataset.animateDuration = '0.4';
        appear.dataset.animateY = '30px';
        if (index === 0) appear.dataset.animateDelay = '0.25';

        const vertical = this.layout === 'vertical';

        const panel = document.createElement('div');
        panel.className = `option-select-panel justify-self-${this.tabsAlignment}${vertical ? ' option-select-panel--vertical' : ''}`;
        const formWidth = vertical ? this.formWidthVertical : this.formWidth;
        panel.style.setProperty('--option-select-form-width', `${formWidth}%`);

        const bar = document.createElement('div');
        const fields = group.querySelector('.catalog-manager-group__fields');
        const fieldCount = fields?.children.length || 0;
        bar.className = vertical
          ? 'option-select-bar option-select-bar--vertical'
          : `option-select-bar option-select-bar--count-${fieldCount}`;

        [...(fields?.children || [])].forEach((block) => {
          const field = block.querySelector('.catalog-manager-group__field') || block;
          const optionSelect = this.enhanceField(field);
          if (optionSelect) bar.appendChild(optionSelect);
        });

        const submit = group.querySelector('[data-catalog-manager-submit]');
        if (submit) {
          submit.classList.add('button', `button--${this.buttonStyle}`, 'button--lg');
          const label = submit.dataset.defaultLabel || submit.textContent.trim() || 'Search';
          const icon = this.showButtonIcon ? this.iconSearch : '';
          submit.innerHTML = `${icon}<span class="catalog-manager-submit-label">${this.escape(label)}</span>`;
          const button = document.createElement('div');
          button.className = 'option-select-button';
          button.appendChild(submit);
          panel.append(bar, button);
        } else {
          panel.appendChild(bar);
        }

        fields?.remove();
        group.querySelector('h3')?.setAttribute('hidden', '');
        group.appendChild(appear);
        appear.appendChild(panel);
        content.appendChild(group);
        this.catalog.appendChild(content);

        this.tabs.push(tab);
        this.panels.push(content);
      });

      if (hasSubheading) {
        this.tabsList.closest('.heading-wrapper__content')?.appendChild(descriptionWrapper);
      }

      this.tabsList.addEventListener('click', (event) => {
        const tab = event.target.closest('.tabs__tab');
        if (!tab) return;
        this.activateTab(this.tabs.indexOf(tab));
      });

      this.closest('animated-stages')?.resetAnimation?.();
    }

    enhanceField(field) {
      const select = field.querySelector('select[data-catalog-manager-filter-field]');
      if (!select) return null;
      select.hidden = true;

      const label = field.querySelector(':scope > span')?.textContent.trim() || 'Select';
      const panelId = `quick-search-${select.dataset.fieldKey || Math.random().toString(36).slice(2)}-panel`;
      const listId = `${panelId}-list`;
      const showSearch = select.options.length - 1 > 10;

      const searchHtml = showSearch
        ? `<div class="option-select__search field"><input class="option-select__search-input field__input field__input--no-label" id="${panelId}-search" type="search" placeholder="${this.escape(this.searchPlaceholder)}" role="combobox" aria-autocomplete="list" autocomplete="off"><button type="reset" class="option-select__search-clear field__reset-button hidden" aria-label="${this.escape(this.resetLabel)}">${this.iconReset}</button><div class="option-select__search-icon field__button field__button--left" aria-hidden="true">${this.iconSearch}</div></div>`
        : '';

      const wrapper = document.createElement('div');
      wrapper.innerHTML = `<option-select class="option-select"><button type="button" class="option-select__toggle button-unstyled" aria-expanded="false" aria-controls="${panelId}"><span class="option-select__value"><span class="option-select__label h6 mb-0"></span><span class="option-select__selected h6 mb-0" aria-hidden="true"></span></span><span class="option-select__caret" aria-hidden="true">${ICON_CARET}</span><span class="option-select__check" aria-hidden="true">${this.iconCheck}</span></button><div class="option-select__panel" id="${panelId}" hidden><div class="option-select__panel-header"><h4 class="option-select__panel-title h4 mb-0">${this.escape(label)}</h4><button type="button" class="option-select__close button-unstyled" aria-label="${this.escape(this.closeLabel)}">${this.iconClose}</button></div><div class="option-select__panel-inner">${searchHtml}<div class="option-select__list-scroll has-custom-scrollbar"><div class="option-select__list-viewport" id="${listId}"><ul class="option-select__list text-uppercase-forms" role="list"></ul><div class="option-select__no-results text-size-2" hidden>${this.escape(this.noResultsText)}</div></div><custom-scrollbar target="#${listId}"><div class="custom-scrollbar__track"><div class="custom-scrollbar__thumb" style="display: none;"></div></div></custom-scrollbar></div></div></div></option-select>`;

      const optionSelect = wrapper.firstElementChild;
      optionSelect.appendChild(select);
      optionSelect._quickSearchLabel = label;
      [...field.attributes].forEach((attr) => {
        if (attr.name.startsWith('data-shopify')) optionSelect.setAttribute(attr.name, attr.value);
      });
      return optionSelect;
    }

    refreshControls() {
      this.querySelectorAll('select[data-catalog-manager-filter-field]').forEach((select) => {
        const optionSelect = select.closest('option-select');
        if (!optionSelect) return;
        const label = optionSelect._quickSearchLabel;
        const selected = select.options[select.selectedIndex];
        const selectedText = select.value ? selected?.textContent.trim() : '';
        optionSelect.querySelector('.option-select__label').textContent = label;
        optionSelect.querySelector('.option-select__selected').textContent = selectedText;
        optionSelect.classList.toggle('is-selected', Boolean(selectedText));
        optionSelect.classList.toggle('is-disabled', select.disabled);
        optionSelect.querySelector('.option-select__toggle').disabled = select.disabled;

        const list = optionSelect.querySelector('.option-select__list');
        list.replaceChildren();

        const options = [...select.options].filter((option) => option.value);
        if (options.length > 10) {
          options.sort((a, b) => Number(a.disabled) - Number(b.disabled));
        }
        options.forEach((option) => {
          const item = document.createElement('li');
          item.className = 'option-select__item';
          const link = document.createElement('a');
          link.href = '#';
          link.className = 'option-select__option text-size-2';
          link.dataset.value = option.value;
          link.dataset.disabled = String(option.disabled);
          link.setAttribute('aria-disabled', String(option.disabled));
          link.classList.toggle('is-disabled', option.disabled);
          link.setAttribute('aria-current', select.value === option.value ? 'true' : 'false');
          link.innerHTML = `<span class="option-select__option-name">${this.escape(option.textContent.trim())}</span>`;
          item.appendChild(link);
          list.appendChild(item);
        });
      });
    }

    handleChange(event) {
      if (event.target.matches('select[data-catalog-manager-filter-field]')) this.refreshControls();
    }

    handleSelect(event) {
      const select = event.target.querySelector('select[data-catalog-manager-filter-field]');
      if (!select) return;
      select.value = event.detail.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    activateTab(index) {
      if (index < 0) return;
      this.tabs.forEach((tab, tabIndex) => {
        const active = index === tabIndex;
        tab.classList.toggle('tabs__tab--active', active);
        tab.tabIndex = active ? 0 : -1;
        this.panels[tabIndex].classList.toggle('hidden', !active);
        this.panels[tabIndex].classList.toggle('tabs__tab-content--active', active);
        this.descriptions[tabIndex]?.classList.toggle('hidden', !active);
      });
      this.showTabContentInstantly(index);
      this.scrollTabIntoView(this.tabs[index]);
    }

    showTabContentInstantly(index) {
      const appear = this.panels[index]?.querySelector('.option-select-appear');
      if (!appear) return;
      appear.querySelectorAll(':scope > *').forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
    }

    scrollTabIntoView(tab) {
      if (!tab || !this.tabsList) return;
      if (this.tabsList.scrollWidth <= this.tabsList.clientWidth) return;
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    escape(value) {
      const element = document.createElement('span');
      element.textContent = value;
      return element.innerHTML;
    }
  }

  customElements.define('quick-search-by-metafields', QuickSearchByMetafields);
}
